import { useState, useEffect } from 'react';
import { useToastStore } from '../store/useToastStore.js';
import { fetchStories, createStory, updateStory, deleteStory, addPracticeLog, resetPracticeHistory } from '../api/client.js';
import { StoryEditor } from '../components/practice/StoryEditor.jsx';
import { PracticeMode } from '../components/practice/PracticeMode.jsx';

export function Practice() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | edit | practice
  const [activeStory, setActiveStory] = useState(null);
  const [search, setSearch] = useState('');
  const addToast = useToastStore((s) => s.addToast);

  useEffect(() => { loadStories(); }, []);

  async function loadStories() {
    try {
      const data = await fetchStories();
      setStories(data);
    } catch { addToast('Failed to load stories', 'error'); }
    finally { setLoading(false); }
  }

  function handleNew() {
    setActiveStory(null);
    setView('edit');
  }

  function handleEdit(story) {
    setActiveStory(story);
    setView('edit');
  }

  function handlePractice(story) {
    setActiveStory(story);
    setView('practice');
  }

  async function handleSave(data) {
    try {
      if (activeStory) {
        const updated = await updateStory(activeStory.storyId, data);
        setStories((s) => s.map((st) => st.storyId === activeStory.storyId ? { ...st, ...updated } : st));
      } else {
        const created = await createStory(data);
        setStories((s) => [...s, created]);
      }
      setView('list');
      addToast('Story saved ✓', 'success');
    } catch { addToast('Failed to save story', 'error'); }
  }

  async function handleDelete(storyId) {
    try {
      await deleteStory(storyId);
      setStories((s) => s.filter((st) => st.storyId !== storyId));
      setView('list');
      addToast('Story deleted ✓', 'success');
    } catch { addToast('Failed to delete story', 'error'); }
  }

  async function handleScore(storyId, score) {
    try {
      const practice = await addPracticeLog(storyId, { score });
      setStories((s) => s.map((st) => st.storyId === storyId
        ? { ...st, practices: [...(st.practices || []), practice] }
        : st
      ));
      setView('list');
      addToast('Practice logged ✓', 'success');
    } catch { addToast('Failed to log practice', 'error'); }
  }

  async function handleReset(storyId) {
    try {
      await resetPracticeHistory(storyId);
      setStories((s) => s.map((st) => st.storyId === storyId ? { ...st, practices: [] } : st));
      addToast('Practice history reset ✓', 'success');
    } catch { addToast('Failed to reset', 'error'); }
  }

  if (view === 'edit') {
    return <StoryEditor story={activeStory} onSave={handleSave} onCancel={() => setView('list')} onDelete={activeStory ? () => handleDelete(activeStory.storyId) : null} />;
  }

  if (view === 'practice' && activeStory) {
    return <PracticeMode story={activeStory} onScore={(score) => handleScore(activeStory.storyId, score)} onCancel={() => setView('list')} />;
  }

  // Story list
  const now = new Date();

  const filtered = stories.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.title.toLowerCase().includes(q) || (s.role || '').toLowerCase().includes(q) || (s.company || '').toLowerCase().includes(q) || (s.notes || '').toLowerCase().includes(q);
  });

  const dueStories = filtered.filter((s) => {
    if (!s.practices?.length) return true;
    return new Date(s.practices[s.practices.length - 1].nextDue) <= now;
  });

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">Practice Stories</h1>
        <button onClick={handleNew} className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700">
          + New Story
        </button>
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search stories…"
        className="w-full max-w-xs rounded border border-slate-300 px-3 py-1.5 text-base focus:outline-none focus:ring-1 focus:ring-primary-500"
      />

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded border border-slate-200 p-4 space-y-2">
              <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
              <div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
            </div>
          ))}
        </div>
      )}

      {!loading && stories.length === 0 && (
        <div className="rounded border border-slate-200 p-6 text-center">
          <p className="text-sm text-slate-500 mb-3">No stories yet. Capture your wins as structured stories for interview practice.</p>
          <button onClick={handleNew} className="text-sm text-primary-600 font-medium hover:underline">Create your first story</button>
        </div>
      )}

      {dueStories.length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-700">🎙️ {dueStories.length} {dueStories.length === 1 ? 'story' : 'stories'} due for practice</p>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((story) => {
          const lastPractice = story.practices?.[story.practices.length - 1];
          const isDue = !lastPractice || new Date(lastPractice.nextDue) <= now;

          return (
            <div key={story.storyId} className={`rounded border p-4 ${isDue ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{story.title}</p>
                  {story.role && <p className="text-xs text-slate-500">{story.role}</p>}
                  <div className="flex gap-3 mt-1 text-xs text-slate-400">
                    {lastPractice && <span>Last: {new Date(lastPractice.date).toLocaleDateString()}</span>}
                    {lastPractice && <span>Score: {lastPractice.score}/5</span>}
                    {story.practices?.length > 0 && <span>{story.practices.length} sessions</span>}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => handlePractice(story)} className="rounded bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700">
                    Practice
                  </button>
                  <button onClick={() => handleEdit(story)} className="rounded border border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50">
                    Edit
                  </button>
                  <button onClick={() => handleReset(story.storyId)} className="rounded px-2.5 py-1 text-xs text-slate-400 hover:text-slate-600">
                    Reset
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
